import { z } from 'zod';
import { decimalSchema } from './prisma-zod';

// =====================
// Compensation Types Enum
// =====================

export enum CompensationType {
  HOURLY = 'HOURLY',
  SALARY_ANNUAL = 'SALARY_ANNUAL',
  SALARY_MONTHLY = 'SALARY_MONTHLY',
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  SEMIMONTHLY = 'SEMIMONTHLY',
}

export enum PayrollTaxType {
  EMPLOYER = 'EMPLOYER',
  EMPLOYEE = 'EMPLOYEE',
}

export enum BenefitType {
  HEALTH_INSURANCE = 'HEALTH_INSURANCE',
  DENTAL_INSURANCE = 'DENTAL_INSURANCE',
  VISION_INSURANCE = 'VISION_INSURANCE',
  RETIREMENT_401K = 'RETIREMENT_401K',
  RETIREMENT_MATCH = 'RETIREMENT_MATCH',
  LIFE_INSURANCE = 'LIFE_INSURANCE',
  DISABILITY_SHORT_TERM = 'DISABILITY_SHORT_TERM',
  DISABILITY_LONG_TERM = 'DISABILITY_LONG_TERM',
  HSA = 'HSA',
  FSA = 'FSA',
  PTO_VALUE = 'PTO_VALUE',
  OTHER = 'OTHER',
}

export enum PayPeriodFrequency {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  SEMIMONTHLY = 'SEMIMONTHLY',
  MONTHLY = 'MONTHLY',
  ANNUAL = 'ANNUAL',
}

export enum TaxOverrideType {
  RATE = 'RATE',
  AMOUNT = 'AMOUNT',
  EXEMPT = 'EXEMPT',
}

// =====================
// Constants
// =====================

export const HOURS_PER_YEAR = 2080; // 40 hours * 52 weeks

export const PAY_PERIODS_PER_YEAR = {
  WEEKLY: 52,
  BIWEEKLY: 26,
  SEMIMONTHLY: 24,
  MONTHLY: 12,
  ANNUAL: 1,
} as const;

export const COMPENSATION_TYPE_LABELS: Record<CompensationType, string> = {
  [CompensationType.HOURLY]: 'Hourly',
  [CompensationType.SALARY_ANNUAL]: 'Salary (Annual)',
  [CompensationType.SALARY_MONTHLY]: 'Salary (Monthly)',
  [CompensationType.WEEKLY]: 'Weekly',
  [CompensationType.BIWEEKLY]: 'Bi-weekly',
  [CompensationType.SEMIMONTHLY]: 'Semi-monthly',
};

export const BENEFIT_TYPE_LABELS: Record<BenefitType, string> = {
  [BenefitType.HEALTH_INSURANCE]: 'Health Insurance',
  [BenefitType.DENTAL_INSURANCE]: 'Dental Insurance',
  [BenefitType.VISION_INSURANCE]: 'Vision Insurance',
  [BenefitType.RETIREMENT_401K]: '401(k) Contribution',
  [BenefitType.RETIREMENT_MATCH]: '401(k) Employer Match',
  [BenefitType.LIFE_INSURANCE]: 'Life Insurance',
  [BenefitType.DISABILITY_SHORT_TERM]: 'Short-term Disability',
  [BenefitType.DISABILITY_LONG_TERM]: 'Long-term Disability',
  [BenefitType.HSA]: 'HSA Contribution',
  [BenefitType.FSA]: 'FSA Contribution',
  [BenefitType.PTO_VALUE]: 'PTO Value',
  [BenefitType.OTHER]: 'Other',
};

// =====================
// Payroll Tax Item Schema
// =====================

export const PayrollTaxItemSchema = z.object({
  id: z.string().uuid(),
  payrollConfigId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  taxType: z.nativeEnum(PayrollTaxType),
  ratePercent: decimalSchema.optional().nullable(),
  flatAmount: decimalSchema.optional().nullable(),
  wageBaseLimit: decimalSchema.optional().nullable(),
  displayOrder: z.number().int().default(0),
  isActive: z.boolean().default(true),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type PayrollTaxItem = z.infer<typeof PayrollTaxItemSchema>;

export const CreatePayrollTaxItemSchema = PayrollTaxItemSchema.omit({
  id: true,
  payrollConfigId: true,
  createdAt: true,
  updatedAt: true,
});

export type CreatePayrollTaxItem = z.infer<typeof CreatePayrollTaxItemSchema>;

export const UpdatePayrollTaxItemSchema = CreatePayrollTaxItemSchema.partial();

export type UpdatePayrollTaxItem = z.infer<typeof UpdatePayrollTaxItemSchema>;

// =====================
// Organization Benefit Template Schema
// =====================

export const OrganizationBenefitTemplateSchema = z.object({
  id: z.string().uuid(),
  payrollConfigId: z.string().uuid(),
  name: z.string(),
  benefitType: z.nativeEnum(BenefitType),
  employerAmount: decimalSchema.optional().nullable(),
  employerPercent: decimalSchema.optional().nullable(),
  employeeAmount: decimalSchema.optional().nullable(),
  employeePercent: decimalSchema.optional().nullable(),
  matchPercent: decimalSchema.optional().nullable(),
  matchUpToPercent: decimalSchema.optional().nullable(),
  matchVestingMonths: z.number().int().optional().nullable(),
  annualLimit: decimalSchema.optional().nullable(),
  displayOrder: z.number().int().default(0),
  isActive: z.boolean().default(true),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type OrganizationBenefitTemplate = z.infer<typeof OrganizationBenefitTemplateSchema>;

export const CreateBenefitTemplateSchema = OrganizationBenefitTemplateSchema.omit({
  id: true,
  payrollConfigId: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateBenefitTemplate = z.infer<typeof CreateBenefitTemplateSchema>;

export const UpdateBenefitTemplateSchema = CreateBenefitTemplateSchema.partial();

export type UpdateBenefitTemplate = z.infer<typeof UpdateBenefitTemplateSchema>;

// =====================
// Organization Payroll Config Schema
// =====================

export const OrganizationPayrollConfigSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  defaultHoursPerWeek: decimalSchema.default(40),
  defaultHoursPerPayPeriod: decimalSchema.default(86.67),
  payPeriodsPerYear: z.number().int().default(26),
  defaultWeeksPerYear: decimalSchema.default(52),
  currency: z.string().default('USD'),
  region: z.string().default('US'),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
  taxItems: z.array(PayrollTaxItemSchema).optional(),
  benefits: z.array(OrganizationBenefitTemplateSchema).optional(),
});

export type OrganizationPayrollConfig = z.infer<typeof OrganizationPayrollConfigSchema>;

export const CreatePayrollConfigSchema = OrganizationPayrollConfigSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  taxItems: true,
  benefits: true,
});

export type CreatePayrollConfig = z.infer<typeof CreatePayrollConfigSchema>;

export const UpdatePayrollConfigSchema = CreatePayrollConfigSchema.partial();

export type UpdatePayrollConfig = z.infer<typeof UpdatePayrollConfigSchema>;

// =====================
// Member Benefit Schema
// =====================

export const MemberBenefitSchema = z.object({
  id: z.string().uuid(),
  compensationId: z.string().uuid(),
  templateId: z.string().uuid(),
  employerAmountOverride: decimalSchema.optional().nullable(),
  employerPercentOverride: decimalSchema.optional().nullable(),
  employeeAmountOverride: decimalSchema.optional().nullable(),
  employeePercentOverride: decimalSchema.optional().nullable(),
  isEnrolled: z.boolean().default(true),
  enrollmentDate: z.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  template: OrganizationBenefitTemplateSchema.optional(),
});

export type MemberBenefit = z.infer<typeof MemberBenefitSchema>;

export const CreateMemberBenefitSchema = MemberBenefitSchema.omit({
  id: true,
  compensationId: true,
  createdAt: true,
  updatedAt: true,
  template: true,
});

export type CreateMemberBenefit = z.infer<typeof CreateMemberBenefitSchema>;

export const UpdateMemberBenefitSchema = CreateMemberBenefitSchema.partial();

export type UpdateMemberBenefit = z.infer<typeof UpdateMemberBenefitSchema>;

// =====================
// Member Tax Override Schema
// =====================

export const MemberTaxOverrideSchema = z.object({
  id: z.string().uuid(),
  compensationId: z.string().uuid(),
  taxCode: z.string(),
  overrideType: z.nativeEnum(TaxOverrideType),
  rateOverride: decimalSchema.optional().nullable(),
  amountOverride: decimalSchema.optional().nullable(),
  isExempt: z.boolean().default(false),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type MemberTaxOverride = z.infer<typeof MemberTaxOverrideSchema>;

export const CreateMemberTaxOverrideSchema = MemberTaxOverrideSchema.omit({
  id: true,
  compensationId: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateMemberTaxOverride = z.infer<typeof CreateMemberTaxOverrideSchema>;

export const UpdateMemberTaxOverrideSchema = CreateMemberTaxOverrideSchema.partial();

export type UpdateMemberTaxOverride = z.infer<typeof UpdateMemberTaxOverrideSchema>;

// =====================
// Member Compensation Schema
// =====================

export const MemberCompensationSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  compensationType: z.nativeEnum(CompensationType).default(CompensationType.HOURLY),
  baseAmount: decimalSchema,
  currency: z.string().default('USD'),
  effectiveHourlyRate: decimalSchema.optional().nullable(),
  hoursPerWeek: decimalSchema.default(40),
  weeksPerYear: decimalSchema.default(52),
  billRate: decimalSchema.optional().nullable(),
  costRate: decimalSchema.optional().nullable(),
  effectiveFrom: z.date(),
  effectiveTo: z.date().optional().nullable(),
  isActive: z.boolean().default(true),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  benefits: z.array(MemberBenefitSchema).optional(),
  taxOverrides: z.array(MemberTaxOverrideSchema).optional(),
  member: z.any().optional(),
});

export type MemberCompensation = z.infer<typeof MemberCompensationSchema>;

export const CreateMemberCompensationSchema = MemberCompensationSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  effectiveHourlyRate: true,
  benefits: true,
  taxOverrides: true,
  member: true,
});

export type CreateMemberCompensation = z.infer<typeof CreateMemberCompensationSchema>;

export const UpdateMemberCompensationSchema = CreateMemberCompensationSchema.partial().omit({
  memberId: true,
  organizationId: true,
});

export type UpdateMemberCompensation = z.infer<typeof UpdateMemberCompensationSchema>;

// =====================
// Compensation Calculation Types
// =====================

export interface TaxBreakdown {
  code: string;
  name: string;
  taxType: PayrollTaxType;
  ratePercent?: number | null;
  flatAmount?: number | null;
  calculatedAmount: number;
  annualAmount: number;
  ytdAmount?: number;
  wageBaseLimit?: number | null;
  wageBaseReached?: boolean;
}

export interface BenefitBreakdown {
  templateId: string;
  benefitType: BenefitType;
  name: string;
  employerAmount: number;
  employeeAmount: number;
  employerAnnual: number;
  employeeAnnual: number;
  isEnrolled: boolean;
}

export interface CompensationBreakdown {
  // Input
  compensationType: CompensationType;
  baseAmount: number;
  hoursPerWeek: number;
  weeksPerYear: number;
  payPeriodsPerYear: number;

  // Calculated Rates
  effectiveHourlyRate: number;
  annualGrossPay: number;

  // Per Pay Period
  payPeriodGrossPay: number;
  payPeriodHours: number;

  // Employee Deductions
  employeeTaxes: TaxBreakdown[];
  employeeBenefits: BenefitBreakdown[];
  totalEmployeeDeductions: number;
  totalEmployeeTaxes: number;
  totalEmployeeBenefitDeductions: number;

  // Net Pay
  payPeriodNetPay: number;
  annualNetPay: number;

  // Employer Costs
  employerTaxes: TaxBreakdown[];
  employerBenefits: BenefitBreakdown[];
  totalEmployerTaxes: number;
  totalEmployerBenefitContributions: number;
  totalEmployerContributions: number;

  // Total Cost to Employer
  payPeriodEmployerCost: number;
  annualEmployerCost: number;
}

// =====================
// Calculation Helper Functions
// =====================

/**
 * Calculate effective hourly rate from any compensation type
 */
export function calculateEffectiveHourlyRate(
  compensationType: CompensationType,
  baseAmount: number,
  hoursPerWeek: number = 40,
  weeksPerYear: number = 52
): number {
  const annualHours = hoursPerWeek * weeksPerYear;

  switch (compensationType) {
    case CompensationType.HOURLY:
      return baseAmount;
    case CompensationType.SALARY_ANNUAL:
      return annualHours > 0 ? baseAmount / annualHours : 0;
    case CompensationType.SALARY_MONTHLY:
      return annualHours > 0 ? (baseAmount * 12) / annualHours : 0;
    case CompensationType.WEEKLY:
      return hoursPerWeek > 0 ? baseAmount / hoursPerWeek : 0;
    case CompensationType.BIWEEKLY:
      return hoursPerWeek > 0 ? baseAmount / (hoursPerWeek * 2) : 0;
    case CompensationType.SEMIMONTHLY:
      return annualHours > 0 ? (baseAmount * 24) / annualHours : 0;
    default:
      return baseAmount;
  }
}

/**
 * Calculate annual gross pay from any compensation type
 */
export function calculateAnnualGross(
  compensationType: CompensationType,
  baseAmount: number,
  hoursPerWeek: number = 40,
  weeksPerYear: number = 52
): number {
  switch (compensationType) {
    case CompensationType.HOURLY:
      return baseAmount * hoursPerWeek * weeksPerYear;
    case CompensationType.SALARY_ANNUAL:
      return baseAmount;
    case CompensationType.SALARY_MONTHLY:
      return baseAmount * 12;
    case CompensationType.WEEKLY:
      return baseAmount * weeksPerYear;
    case CompensationType.BIWEEKLY:
      return baseAmount * 26;
    case CompensationType.SEMIMONTHLY:
      return baseAmount * 24;
    default:
      return baseAmount;
  }
}

/**
 * Calculate pay per period from annual gross
 */
export function calculatePayPeriodAmount(
  annualGross: number,
  payPeriodFrequency: PayPeriodFrequency
): number {
  const periods = PAY_PERIODS_PER_YEAR[payPeriodFrequency];
  return periods > 0 ? annualGross / periods : 0;
}

/**
 * Calculate hours per pay period
 */
export function calculateHoursPerPayPeriod(
  hoursPerWeek: number,
  payPeriodFrequency: PayPeriodFrequency
): number {
  switch (payPeriodFrequency) {
    case PayPeriodFrequency.WEEKLY:
      return hoursPerWeek;
    case PayPeriodFrequency.BIWEEKLY:
      return hoursPerWeek * 2;
    case PayPeriodFrequency.SEMIMONTHLY:
      // Average: (hours per year) / 24
      return (hoursPerWeek * 52) / 24;
    case PayPeriodFrequency.MONTHLY:
      return (hoursPerWeek * 52) / 12;
    case PayPeriodFrequency.ANNUAL:
      return hoursPerWeek * 52;
    default:
      return hoursPerWeek * 2; // Default to biweekly
  }
}

/**
 * Calculate tax amount for a single tax item
 */
export function calculateTaxAmount(
  taxItem: PayrollTaxItem,
  grossPayPeriod: number,
  annualGross: number,
  ytdGross: number = 0
): number {
  // Check if wage base limit is reached
  if (taxItem.wageBaseLimit) {
    const wageBase = Number(taxItem.wageBaseLimit);
    if (ytdGross >= wageBase) {
      return 0; // Already reached wage base
    }
    // Calculate taxable amount respecting wage base
    const remainingWageBase = wageBase - ytdGross;
    const taxableAmount = Math.min(grossPayPeriod, remainingWageBase);

    if (taxItem.ratePercent) {
      return taxableAmount * (Number(taxItem.ratePercent) / 100);
    }
  }

  // Calculate based on rate or flat amount
  if (taxItem.ratePercent) {
    return grossPayPeriod * (Number(taxItem.ratePercent) / 100);
  }

  if (taxItem.flatAmount) {
    return Number(taxItem.flatAmount);
  }

  return 0;
}

/**
 * Format currency value
 */
export function formatCompensationCurrency(
  value: number,
  currency: string = 'USD',
  decimals: number = 2
): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Get compensation type input label
 */
export function getCompensationInputLabel(type: CompensationType): string {
  switch (type) {
    case CompensationType.HOURLY:
      return 'Hourly Rate';
    case CompensationType.SALARY_ANNUAL:
      return 'Annual Salary';
    case CompensationType.SALARY_MONTHLY:
      return 'Monthly Salary';
    case CompensationType.WEEKLY:
      return 'Weekly Pay';
    case CompensationType.BIWEEKLY:
      return 'Bi-weekly Pay';
    case CompensationType.SEMIMONTHLY:
      return 'Semi-monthly Pay';
    default:
      return 'Base Amount';
  }
}

/**
 * Get compensation type unit suffix
 */
export function getCompensationUnitSuffix(type: CompensationType): string {
  switch (type) {
    case CompensationType.HOURLY:
      return '/hour';
    case CompensationType.SALARY_ANNUAL:
      return '/year';
    case CompensationType.SALARY_MONTHLY:
      return '/month';
    case CompensationType.WEEKLY:
      return '/week';
    case CompensationType.BIWEEKLY:
      return '/2 weeks';
    case CompensationType.SEMIMONTHLY:
      return '/semi-month';
    default:
      return '';
  }
}

// =====================
// Default US Tax Configuration
// =====================

export const DEFAULT_US_TAX_ITEMS: Omit<PayrollTaxItem, 'id' | 'payrollConfigId' | 'createdAt' | 'updatedAt'>[] = [
  // Employer Taxes
  {
    code: 'SS_ER',
    name: 'Social Security (Employer)',
    taxType: PayrollTaxType.EMPLOYER,
    ratePercent: 6.2,
    flatAmount: null,
    wageBaseLimit: 168600,
    displayOrder: 1,
    isActive: true,
    notes: 'Social Security tax paid by employer',
  },
  {
    code: 'MED_ER',
    name: 'Medicare (Employer)',
    taxType: PayrollTaxType.EMPLOYER,
    ratePercent: 1.45,
    flatAmount: null,
    wageBaseLimit: null,
    displayOrder: 2,
    isActive: true,
    notes: 'Medicare tax paid by employer (no wage base limit)',
  },
  {
    code: 'FUTA',
    name: 'Federal Unemployment (FUTA)',
    taxType: PayrollTaxType.EMPLOYER,
    ratePercent: 0.6,
    flatAmount: null,
    wageBaseLimit: 7000,
    displayOrder: 3,
    isActive: true,
    notes: 'Federal unemployment tax (0.6% after state credit)',
  },
  {
    code: 'SUTA',
    name: 'State Unemployment (SUTA)',
    taxType: PayrollTaxType.EMPLOYER,
    ratePercent: 2.7,
    flatAmount: null,
    wageBaseLimit: 7000,
    displayOrder: 4,
    isActive: true,
    notes: 'State unemployment tax (varies by state, 2.7% is average)',
  },
  // Employee Taxes
  {
    code: 'SS_EE',
    name: 'Social Security (Employee)',
    taxType: PayrollTaxType.EMPLOYEE,
    ratePercent: 6.2,
    flatAmount: null,
    wageBaseLimit: 168600,
    displayOrder: 5,
    isActive: true,
    notes: 'Social Security tax withheld from employee',
  },
  {
    code: 'MED_EE',
    name: 'Medicare (Employee)',
    taxType: PayrollTaxType.EMPLOYEE,
    ratePercent: 1.45,
    flatAmount: null,
    wageBaseLimit: null,
    displayOrder: 6,
    isActive: true,
    notes: 'Medicare tax withheld from employee (no wage base limit)',
  },
  {
    code: 'FIT',
    name: 'Federal Income Tax',
    taxType: PayrollTaxType.EMPLOYEE,
    ratePercent: 22,
    flatAmount: null,
    wageBaseLimit: null,
    displayOrder: 7,
    isActive: true,
    notes: 'Simplified federal income tax rate (actual varies by bracket)',
  },
  {
    code: 'SIT',
    name: 'State Income Tax',
    taxType: PayrollTaxType.EMPLOYEE,
    ratePercent: 5,
    flatAmount: null,
    wageBaseLimit: null,
    displayOrder: 8,
    isActive: true,
    notes: 'Placeholder state income tax (varies by state)',
  },
];

// =====================
// Default Benefit Templates
// =====================

export const DEFAULT_BENEFIT_TEMPLATES: Omit<OrganizationBenefitTemplate, 'id' | 'payrollConfigId' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'Health Insurance',
    benefitType: BenefitType.HEALTH_INSURANCE,
    employerAmount: 400,
    employerPercent: null,
    employeeAmount: 150,
    employeePercent: null,
    matchPercent: null,
    matchUpToPercent: null,
    matchVestingMonths: null,
    annualLimit: null,
    displayOrder: 1,
    isActive: true,
    notes: 'Monthly health insurance premium (employer pays $400, employee pays $150)',
  },
  {
    name: 'Dental Insurance',
    benefitType: BenefitType.DENTAL_INSURANCE,
    employerAmount: 30,
    employerPercent: null,
    employeeAmount: 15,
    employeePercent: null,
    matchPercent: null,
    matchUpToPercent: null,
    matchVestingMonths: null,
    annualLimit: null,
    displayOrder: 2,
    isActive: true,
    notes: 'Monthly dental insurance premium',
  },
  {
    name: '401(k) Employer Match',
    benefitType: BenefitType.RETIREMENT_MATCH,
    employerAmount: null,
    employerPercent: null,
    employeeAmount: null,
    employeePercent: null,
    matchPercent: 100,
    matchUpToPercent: 4,
    matchVestingMonths: 12,
    annualLimit: 23000,
    displayOrder: 3,
    isActive: true,
    notes: 'Employer matches 100% of employee contribution up to 4% of salary',
  },
];
